import React, { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import CustomModal from "../../common/CustomModal";
import AdsAPIs from "../../../apiRoutes/Ads";
import AgencyAPIs from "../../../apiRoutes/Agency";
import { GET_AGENCIES_QUERY_KEY, GET_AD_TYPES_QUERY_KEY, GET_RATE_CARDS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import toast from "react-hot-toast";
import useZustandStore from "../../../zustandStore/useZustandStore";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../../utils/constants/permissions";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../../utils/agencyScope";

const RateCardModal = ({ open, onClose, editData = null, agencyScoped: agencyScopedProp, scopedAgencyId: scopedAgencyIdProp }) => {
  const [formData, setFormData] = useState({
    agency_id: editData?.agency_id ?? "",
    adtype_id: editData?.adtype_id ?? "",
    price_per_unit: editData?.price_per_unit ?? "",
    currency: editData?.currency ?? "INR",
  });
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const { hasPermission } = usePermissions();
  const canListAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);
  const agencyScoped = agencyScopedProp ?? isAgencyScopedUser(currentUser);
  const scopedAgencyId = scopedAgencyIdProp ?? getAgencyIdFromUser(currentUser);

  useEffect(() => {
    if (editData) {
      setFormData({
        agency_id: String(editData.agency_id ?? ""),
        adtype_id: String(editData.adtype_id ?? ""),
        price_per_unit: editData.price_per_unit ?? "",
        currency: editData.currency ?? "INR",
      });
    } else {
      setFormData({ agency_id: "", adtype_id: "", price_per_unit: "", currency: "INR" });
    }
  }, [editData, open]);

  useEffect(() => {
    if (open && agencyScoped && scopedAgencyId != null && !editData) {
      setFormData((f) => ({ ...f, agency_id: String(scopedAgencyId) }));
    }
  }, [open, agencyScoped, scopedAgencyId, editData]);

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: 200, offset: 0 }),
    enabled: open && !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const { data: adtypesResult } = useQuery({
    queryKey: GET_AD_TYPES_QUERY_KEY,
    queryFn: () => AdsAPIs.listAdtypes({ axiosPrivate, limit: 200, offset: 0 }),
    enabled: open,
  });
  const adtypes = adtypesResult?.data ?? [];

  const readOnlyAgencyLabel = (() => {
    if (agencyScoped) return "—";
    if (editData?.agency_id != null) {
      const idx = agencies.findIndex((a) => a.id === editData.agency_id);
      const a = idx >= 0 ? agencies[idx] : null;
      if (a) return agencyDisplayName(a, idx);
    }
    return "—";
  })();

  const { mutate: submit, isPending } = useMutation({
    mutationFn: (payload) =>
      editData
        ? AdsAPIs.updateRateCard({ axiosPrivate, id: editData.id, payload })
        : AdsAPIs.createRateCard({ axiosPrivate, payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_RATE_CARDS_QUERY_KEY });
      toast.success(editData ? "Rate card updated." : "Rate card created.");
      onClose();
    },
    onError: (e) => {
      toast.error(e?.response?.data?.error_message || e?.message);
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.agency_id || !formData.adtype_id) {
      toast.error("Agency and ad type are required.");
      return;
    }
    const payload = {
      agency_id: Number(formData.agency_id),
      adtype_id: Number(formData.adtype_id),
      price_per_unit: formData.price_per_unit ? String(formData.price_per_unit).trim() : null,
      currency: (formData.currency || "").trim() || "INR",
    };
    submit(payload);
  };

  return (
    <CustomModal
      open={open}
      title={editData ? "Edit Rate Card" : "Add Rate Card"}
      onClose={onClose}
      handleConfirmation={handleSubmit}
      handleConfirmationBtnText={editData ? "Update" : "Create"}
      isLoading={isPending}
    >
      <form onSubmit={handleSubmit} className="space-y-4 py-2">
        <div className="space-y-2">
          <Label>Agency *</Label>
          {agencyScoped || editData ? (
            <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 text-sm">
              {readOnlyAgencyLabel}
            </div>
          ) : canListAgencies ? (
            <Select
              value={formData.agency_id}
              onValueChange={(v) => setFormData((f) => ({ ...f, agency_id: v }))}
              disabled={!!editData}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select agency" />
              </SelectTrigger>
              <SelectContent>
                {agencies.map((a, i) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {agencyDisplayName(a, i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="flex h-10 w-full items-center rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground">
              Agency list requires agency management permission.
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label>Ad type *</Label>
          <Select
            value={formData.adtype_id}
            onValueChange={(v) => setFormData((f) => ({ ...f, adtype_id: v }))}
            disabled={!!editData}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select ad type" />
            </SelectTrigger>
            <SelectContent>
              {adtypes.map((t) => (
                <SelectItem key={t.id} value={String(t.id)}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Price per unit</Label>
          <Input
            type="text"
            inputMode="decimal"
            placeholder="e.g. 100.50"
            value={formData.price_per_unit}
            onChange={(e) => setFormData((f) => ({ ...f, price_per_unit: e.target.value }))}
          />
        </div>
        <div className="space-y-2">
          <Label>Currency</Label>
          <Input
            placeholder="e.g. INR"
            value={formData.currency}
            onChange={(e) => setFormData((f) => ({ ...f, currency: e.target.value }))}
          />
        </div>
      </form>
    </CustomModal>
  );
};

export default RateCardModal;
